// src/features/locations/buildDiffPayload.js
//
// Location-edit diff helper. Originally lived inside
// EditLocationModal.jsx; extracted here in LP1.3 Gate 2 so the
// well-tested helper survives the modal's retirement and is
// consumable by the new inline LocationDetailsPanel edit flow.
//
// Field set: spans the FULL editable schema (not just the 11
// fields shown by LocationDetailsPanel inline). The panel can only
// mutate those 11 — the 5 unsurfaced fields (currency, district,
// floors, lat, lng) stay at their prefill values, so they never
// appear in the diff. See tasks/todo.md for the deferred backlog
// item that will surface them.
//
// Stage + Health are NOT part of this diff path — they're edited
// inline via the StageBadge / HealthBadge pill dropdowns (LP1.2
// Gate 1) and write through their own client methods
// (updateStage / updateHealth). They remain in EDITABLE_FIELDS for
// historical parity with the modal so the diff helper still
// recognizes a manual `health` mutation if any future caller
// passes one — the inline panel never does.
//
// Behavior preserved verbatim from EditLocationModal:
//   - Required fields: trimmed string compare; differs → diff.
//   - Health enum: "" → null; differs → diff.
//   - Optional strings: trimmed; "" against non-null prev → null
//     (clears the column). No diff if both blank.
//   - Numerics: parsed via parseNumericField BEFORE comparison so
//     "100" vs 100 is not a false positive. null is a valid clear.

import { parseNumericField } from "../../utils/parseNumericField.js";

// Required fields — same set the server enforces on POST. PATCH
// allows partial updates, but if the user has BLANKED a previously-
// required value we still want to block submission so they don't
// accidentally strip the row's identity. The inline panel currently
// surfaces market / country / region / address from this list (currency
// + stage are not exposed inline — see Phase-2 backlog).
// ADDR1: address promoted from optional to required.
export const REQUIRED_FIELDS = ["market", "country", "currency", "region", "stage", "address"];

// Numeric fields routed through parseNumericField on submit. Empty /
// NaN → null (omitted from diff unless the location previously had
// a value and the user explicitly cleared it, in which case we send
// null).
// LSE-EDIT-LOCATION-REMOVE-SQFT-FIELD: sqft is intentionally excluded —
// it is a derived cache maintained by lease triggers, not a user-editable
// field. The Edit Location save path must never write sqft directly.
// PLAN-TIMELINE-COVERAGE-S1: plannedSeats is a nullable INTEGER column
// (migration 0271), same clear-to-null semantics as the other numerics.
export const NUMERIC_FIELDS = ["installedCapacity", "maxCapacity", "floors", "lat", "lng", "plannedSeats"];

// Optional string fields. Empty → null (and only sent when the
// location previously had a value).
// ADDR1: address promoted to REQUIRED_FIELDS; only optional strings listed here.
// PLAN-TIMELINE-COVERAGE-S1: onlineDate/closeDate are DATE columns
// (migration 0271), stored as "YYYY-MM-DD" string in JSON. Treating them as
// optional strings is correct: the server passes the value directly to pg,
// which casts text→DATE; "" → null clears it.
// FDOB-TO-ONLINEDATE-CONSOLIDATE-S1: the legacy "fdob" field is retired in
// favor of onlineDate (column dropped from locations table).
export const OPTIONAL_STRING_FIELDS = [
  "locationAlias", "mAndA", "district", "suite", "onlineDate", "closeDate",
  "exitDate", "reopenDate",
];

// DATE columns among OPTIONAL_STRING_FIELDS — the server returns these as
// full ISO timestamps, but <input type="date"> and the diff comparison both
// need "YYYY-MM-DD". Any field in this set gets normalizeDateField applied
// to its stored/prev value before comparison.
const DATE_FIELDS = new Set(["onlineDate", "closeDate", "exitDate", "reopenDate"]);

// Editable form fields = the union of the above + the always-present
// required + the health select. id is rendered read-only, NOT in this
// list. imageUrl is owned by PhotoPanel's upload flow, NOT editable
// here.
export const EDITABLE_FIELDS = [
  ...REQUIRED_FIELDS,        // market/country/currency/region/stage
  "health",                  // optional select
  ...OPTIONAL_STRING_FIELDS, // locationAlias/mAndA/district/address/suite
  ...NUMERIC_FIELDS,         // installedCapacity/maxCapacity/floors/lat/lng (sqft excluded — derived)
];

// Boolean fields — compared as booleans, not strings. Stored in the draft
// as actual booleans (not coerced by toFormValue). buildPrefill sets them
// explicitly after the EDITABLE_FIELDS loop so they're not passed through
// toFormValue. HUB-LOCATION-S1: isHub is the first entry.
export const BOOLEAN_FIELDS = ["isHub"];

// Coerce a value from the location object into a string suitable for
// controlled <input value={...}>. null/undefined → "", numbers →
// string.
export function toFormValue(v) {
  if (v == null) return "";
  return String(v);
}

// Postgres DATE columns are returned by node-postgres as full ISO
// timestamps ("2026-05-15T00:00:00.000Z"). <input type="date"> only
// accepts "YYYY-MM-DD", so any timestamp form must be sliced down.
// Exported so buildDiffPayload can normalize the stored prev value too,
// avoiding false-positive diffs on every save when a date field is unchanged.
export function normalizeDateField(v) {
  if (v == null) return "";
  const s = String(v);
  return s.length > 10 && s[10] === "T" ? s.slice(0, 10) : s;
}

// Build the prefill state from a location row. Every editable field
// gets a defined string value so React inputs stay controlled across
// the edit session's open/close lifecycle.
export function buildPrefill(location) {
  const out = {};
  for (const f of EDITABLE_FIELDS) {
    out[f] = toFormValue(location?.[f]);
  }
  // DATE_FIELDS must be "YYYY-MM-DD" for the date input to display the value.
  for (const f of DATE_FIELDS) {
    out[f] = normalizeDateField(location?.[f]);
  }
  // BOOLEAN_FIELDS are stored as actual booleans, not coerced to strings.
  for (const f of BOOLEAN_FIELDS) {
    out[f] = Boolean(location?.[f]);
  }
  return out;
}

// Compute the diff payload: only fields whose draft value differs
// from the stored location value are included. String fields are
// trimmed. Numeric fields are parsed via parseNumericField BEFORE
// comparison so "100" vs 100 isn't a false positive. Health select
// treats "" as null.
//
// Special case: an OPTIONAL field cleared by the user (draft "" against
// a non-null stored value) sends null to the server so it can clear
// the column. A REQUIRED field that's been blanked is caught by the
// caller's validate() before this is invoked.
export function buildDiffPayload(location, draft) {
  const diff = {};
  for (const f of REQUIRED_FIELDS) {
    const next = String(draft[f] ?? "").trim();
    const prev = String(location?.[f] ?? "").trim();
    if (next !== prev) diff[f] = next;
  }
  // health is an enum; "" → null to allow clearing.
  {
    const next = String(draft.health ?? "").trim();
    const prev = String(location?.health ?? "").trim();
    if (next !== prev) diff.health = next === "" ? null : next;
  }
  for (const f of OPTIONAL_STRING_FIELDS) {
    const nextRaw = String(draft[f] ?? "").trim();
    // DATE_FIELDS: normalize the stored value to "YYYY-MM-DD" before comparing
    // so a saved timestamp ("2026-05-15T00:00:00.000Z") doesn't create a
    // false-positive diff against the already-normalized draft value.
    const prevRaw = DATE_FIELDS.has(f)
      ? normalizeDateField(location?.[f])
      : String(location?.[f] ?? "").trim();
    if (nextRaw !== prevRaw) {
      diff[f] = nextRaw === "" ? null : nextRaw;
    }
  }
  for (const f of NUMERIC_FIELDS) {
    const nextNum = parseNumericField(draft[f]);
    const prevNum =
      location?.[f] == null || location[f] === ""
        ? null
        : parseNumericField(location[f]);
    if (nextNum !== prevNum) diff[f] = nextNum; // null is a valid clear
  }
  // BOOLEAN_FIELDS: compare as booleans.
  for (const f of BOOLEAN_FIELDS) {
    const nextBool = Boolean(draft[f]);
    const prevBool = Boolean(location?.[f]);
    if (nextBool !== prevBool) diff[f] = nextBool;
  }
  return diff;
}
